import React, { useCallback, useMemo } from "react";
import { View, StyleSheet, StyleProp, ViewStyle, ColorValue } from "react-native";
import ListItem, { ListItemProps } from "../ui_atoms/ListItem";
import { useTheme } from "../hooks/use-theme";

type ListGroupAlignment = "leading" | "center" | "trailing" | "stretch";

export type ListGroupItem<T> = {
    value: T;
} & ListItemProps;

export type ListGroupProps<T> = {
    alignment?: ListGroupAlignment;
    style?: StyleProp<ViewStyle>;
    itemStyle?: ViewStyle;
    value?: T | null;
    onPressItem?: (val: T) => void;
    items: ListGroupItem<T>[];
    testID?: string;
    borderColor?: ColorValue;
    isItemsCheckable?: boolean;
};

const ALIGNMENT_MAP: Record<ListGroupAlignment, StyleProp<ViewStyle>> = {
    leading: { alignItems: "flex-start" },
    center: { alignItems: "center" },
    trailing: { alignItems: "flex-end" },
    stretch: { alignItems: "stretch" },
};

const styles = StyleSheet.create({
    group: {
        flexDirection: "column",
        width: "100%",
    },
});

const ListGroup = <T extends string>({
    alignment = "leading",
    style,
    itemStyle,
    items,
    value,
    onPressItem: propsOnPressItem,
    isItemsCheckable = true,
    borderColor,
    testID,
}: ListGroupProps<T>) => {
    const onPressItem = useCallback(
        (value: T) => {
            void propsOnPressItem?.(value);
        },
        [propsOnPressItem],
    );
    const listGroupStyle = useListGroupStyle();

    return (
        <View testID={testID} style={[styles.group, ALIGNMENT_MAP[alignment], style]}>
            {items.map(({ style: _itemStyle, ...item }, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === items.length - 1;
                return (
                    <ListItem
                        style={[
                            {
                                borderRadius: 0,
                                width: "100%",
                            },
                            itemStyle,
                            _itemStyle,
                            ...(isFirst ? [listGroupStyle.firstItem] : []),
                            ...(!isFirst ? [listGroupStyle.middleItem] : []),
                            ...(isLast ? [listGroupStyle.lastItem] : []),
                            { borderColor },
                        ]}
                        key={`${item.title}-${item.value}`}
                        testID={item.testID}
                        onPress={
                            item.onPress || propsOnPressItem
                                ? () => onPressItem(item.value)
                                : undefined
                        }
                        checked={
                            isItemsCheckable && value != undefined
                                ? value === item.value
                                : undefined
                        }
                        {...item}
                    />
                );
            })}
        </View>
    );
};

const useListGroupStyle = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                headerText: {
                    ...fonts.headlineSmall,
                },
                bodyText: {
                    ...fonts.bodyMedium,
                },
                firstItem: {},
                middleItem: {
                    borderTopColor: colors.accentLighter,
                },
                lastItem: {
                    borderBottomColor: colors.accentLighter,
                },
            }),
        [fonts.headlineSmall, fonts.bodyMedium, colors.accentLighter],
    );
};

export default ListGroup;
